// aiChatApi.js
import axios from 'axios';
import config from './config';

const AI_CHAT_API_URL = `${config.API_BASE_URL}/ai-chat`;

console.log('🔧 AI Chat Configuration (HTTP Only):');
console.log('  - REST API:', AI_CHAT_API_URL);

// ==================== REST API ENDPOINTS ====================

export const createConversation = async ({ userId = null, branchId = null, language = 'en', currency = 'YER' } = {}) => {
  try {
    const response = await axios.post(`${AI_CHAT_API_URL}/conversations`, {
      userId,
      branchId,
      language,
      currency
    });
    return response.data;
  } catch (error) {
    console.error('❌ Create conversation error:', error);
    throw error;
  }
};

export const sendMessage = async (conversationId, message, currency = null) => {
  try {
    const payload = { conversationId, message };
    if (currency) payload.currency = currency;
    
    console.log('📤 Sending HTTP message:', payload);
    
    const response = await axios.post(`${AI_CHAT_API_URL}/messages`, payload);
    
    console.log('✅ HTTP response:', response.data);
    
    return response.data;
  } catch (error) {
    console.error('❌ Send message error:', error);
    throw error;
  }
};

export const getConversation = async (conversationId) => {
  try {
    const response = await axios.get(`${AI_CHAT_API_URL}/conversations/${conversationId}`);
    return response.data;
  } catch (error) {
    console.error('❌ Get conversation error:', error);
    throw error;
  }
};

export const getUserConversations = async (userId) => {
  try {
    const response = await axios.get(`${AI_CHAT_API_URL}/users/${userId}/conversations`);
    return response.data;
  } catch (error) {
    console.error('❌ Get user conversations error:', error);
    throw error;
  }
};

export const rateConversation = async (conversationId, rating) => {
  try {
    const response = await axios.put(`${AI_CHAT_API_URL}/conversations/${conversationId}/rate`, { rating });
    return response.data;
  } catch (error) {
    console.error('❌ Rate conversation error:', error);
    throw error;
  }
};

export const searchProductsViaChat = async (query, branchId = null, currency = 'YER') => {
  try {
    let url = `${AI_CHAT_API_URL}/search/products?query=${encodeURIComponent(query)}&currency=${currency}`;
    if (branchId) url += `&branchId=${branchId}`;
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('❌ Search products error:', error);
    throw error;
  }
};

export const getAIChatStatistics = async () => {
  try {
    const response = await axios.get(`${AI_CHAT_API_URL}/statistics`);
    return response.data;
  } catch (error) {
    console.error('❌ Get statistics error:', error);
    throw error;
  }
};

// ==================== UTILITY FUNCTIONS ====================

export const formatChatTimestamp = (timestamp) => {
  if (!timestamp) return '';

  const date = new Date(timestamp);
  const now = new Date();
  const diff = now - date;

  if (diff < 60000) return 'Just now';
  
  if (diff < 3600000) {
    const minutes = Math.floor(diff / 60000);
    return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
  }

  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (diff < 604800000) {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const getConversationStatusBadge = (status) => {
  const badges = {
    active: { color: 'success', label: 'Active', icon: '🟢' },
    resolved: { color: 'default', label: 'Resolved', icon: '✅' },
    escalated: { color: 'warning', label: 'Human Support', icon: '👤' }
  };

  return badges[status] || badges.active;
};

export const isValidMessage = (message) => {
  if (!message || typeof message !== 'string') return false;
  const trimmed = message.trim();
  return trimmed.length > 0 && trimmed.length <= 1000;
};

export const cleanSuggestionText = (suggestionText) => {
  if (!suggestionText) return '';
  return suggestionText.replace(/^[^\w\s]+\s*/, '').trim();
};

export const getErrorMessage = (error, language = 'en') => {
  const messages = {
    en: {
      connection: 'Connection failed. Please check your internet.',
      timeout: 'Request timed out. Please try again.',
      server: 'Server error. Please try again later.',
      unauthorized: 'Unauthorized access.',
      notFound: 'Conversation not found.',
      rateLimit: 'Too many requests. Please slow down.',
      default: 'Something went wrong. Please try again.'
    },
    ar: {
      connection: 'فشل الاتصال. يرجى التحقق من الإنترنت.',
      timeout: 'انتهت مهلة الطلب. يرجى المحاولة مرة أخرى.',
      server: 'خطأ في الخادم. يرجى المحاولة لاحقاً.',
      unauthorized: 'وصول غير مصرح به.',
      notFound: 'لم يتم العثور على المحادثة.',
      rateLimit: 'طلبات كثيرة جداً. يرجى الإبطاء.',
      default: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.'
    }
  };

  const lang = messages[language] || messages.en;

  if (error.message?.includes('timeout')) return lang.timeout;
  if (error.message?.includes('Network Error')) return lang.connection;
  if (error.response?.status === 401) return lang.unauthorized;
  if (error.response?.status === 404) return lang.notFound;
  if (error.response?.status === 429) return lang.rateLimit;
  if (error.response?.status >= 500) return lang.server;

  return lang.default;
};

export default {
  createConversation,
  sendMessage,
  getConversation,
  getUserConversations,
  rateConversation,
  searchProductsViaChat,
  getAIChatStatistics,
  formatChatTimestamp,
  getConversationStatusBadge,
  isValidMessage,
  cleanSuggestionText,
  getErrorMessage
};